import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { environment } from '../../../../environments/environment';
import { UnassignedTicketsService } from '../../../core/services/unassigned-tickets.service';
import {
  UnassignedTicket,
  UnassignedTicketListResponse,
} from '../../../core/models/unassigned-ticket.model';
import { Organization } from '../../../core/models/organization.model';
import { AssignTicketModalComponent } from './assign-ticket-modal/assign-ticket-modal.component';

/**
 * Unassigned Tickets Component
 *
 * Displays a paginated list of support tickets that have not been
 * assigned to an organization. Allows admins to assign tickets
 * to organizations via a modal.
 *
 * @see agent-os/specs/2026-01-23-unassigned-tickets/spec.md
 */
@Component({
  selector: 'app-unassigned-tickets',
  standalone: true,
  imports: [CommonModule, FormsModule, AssignTicketModalComponent, PhoneFormatPipe],
  templateUrl: './unassigned-tickets.component.html',
  styleUrls: ['./unassigned-tickets.component.scss']
})
export class UnassignedTicketsComponent implements OnInit {
  tickets: UnassignedTicket[] = [];
  loading = true;
  errorMessage = '';
  unassignedCount = 0;

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalTickets = 0;
  totalPages = 0;

  // Modal state
  selectedTicket: UnassignedTicket | null = null;
  showAssignModal = false;

  constructor(private unassignedTicketsService: UnassignedTicketsService) {}

  ngOnInit(): void {
    this.loadTickets();
    this.loadCount();
  }

  /**
   * Load paginated unassigned tickets list
   */
  async loadTickets(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const response = await this.unassignedTicketsService.getUnassignedTickets({
        page: this.currentPage,
        limit: this.pageSize,
      });

      this.tickets = response.tickets;
      this.totalTickets = response.total;
      this.totalPages = response.totalPages;
    } catch (error: any) {
      console.error('Failed to load unassigned tickets:', error);
      this.errorMessage = 'Failed to load unassigned tickets. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Load unassigned ticket count for badge display
   */
  async loadCount(): Promise<void> {
    try {
      this.unassignedCount = await this.unassignedTicketsService.getUnassignedCount();
    } catch (error: any) {
      console.error('Failed to load unassigned count:', error);
    }
  }

  /**
   * Navigate to a specific page
   */
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTickets();
    }
  }

  /**
   * Open the assign modal for a specific ticket
   */
  openAssignModal(ticket: UnassignedTicket): void {
    this.selectedTicket = ticket;
    this.showAssignModal = true;
  }

  /**
   * Handle modal close
   */
  onModalClosed(): void {
    this.showAssignModal = false;
    this.selectedTicket = null;
  }

  /**
   * Handle successful ticket assignment
   */
  onTicketAssigned(): void {
    this.showAssignModal = false;
    this.selectedTicket = null;
    this.loadTickets();
    this.loadCount();
  }

  /**
   * Get Bootstrap badge class for ticket status
   */
  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-success';
      case 'in progress':
      case 'in_progress':
        return 'bg-primary';
      case 'on hold':
      case 'on_hold':
        return 'bg-warning';
      case 'closed':
        return 'bg-secondary';
      case 'escalated':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get Bootstrap badge class for ticket priority
   */
  getPriorityBadgeClass(priority: string): string {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-danger';
      case 'medium':
        return 'bg-warning';
      case 'low':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}
